import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const WORDS = ['automatyzują.', 'skalują się.', 'przewidują.', 'zarabiają.'];

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % WORDS.length);
        setVisible(true);
      }, 400);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMousePos({
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 30,
        y: ((e.clientY - rect.top) / rect.height - 0.5) * 20,
      });
    };
    window.addEventListener('mousemove', handleMouse);
    return () => window.removeEventListener('mousemove', handleMouse);
  }, []);

  return (
    <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* VIDEO BG */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        poster="https://public.readdy.ai/ai/img_res/1f91d0f110138f34922b686efd034e37.jpg"
      >
        <source src="https://assets.mixkit.co/videos/preview/mixkit-digital-animation-of-futuristic-devices-99786-large.mp4" type="video/mp4" />
      </video>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#0a0a0a]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />

      {/* Animated grid */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,212,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.4) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          transform: `translate(${mousePos.x * 0.3}px, ${mousePos.y * 0.3}px)`,
          transition: 'transform 0.1s ease-out',
        }}
      />

      {/* Floating orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,212,255,0.15) 0%, transparent 70%)',
          transform: `translate(${mousePos.x * 0.8}px, ${mousePos.y * 0.8}px)`,
          transition: 'transform 0.15s ease-out',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(0,255,136,0.12) 0%, transparent 70%)',
          transform: `translate(${-mousePos.x * 0.6}px, ${-mousePos.y * 0.6}px)`,
          transition: 'transform 0.15s ease-out',
          filter: 'blur(40px)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 text-center pt-24 md:pt-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8 animate-fade-in-down">
          <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
          <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Studio AI & Digital · Bieszczady / Polska</span>
        </div>

        {/* Headline */}
        <h1 className="text-6xl md:text-8xl lg:text-[110px] font-black leading-[0.9] tracking-tight mb-6 animate-fade-in-up">
          <span className="block text-white">Biznesy, które</span>
          <span
            className="block text-transparent bg-clip-text"
            style={{
              backgroundImage: 'linear-gradient(135deg, #00d4ff 0%, #00ff88 50%, #00d4ff 100%)',
              backgroundSize: '200% 200%',
              animation: 'gradientShift 3s ease infinite',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}
          >
            {WORDS[wordIndex]}
          </span>
        </h1>

        <p className="text-lg md:text-xl text-white/60 font-light max-w-2xl mx-auto mb-12 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          Wdrażamy nowoczesne technologie AI dla firm, które chcą działać szybciej, mądrzej i skuteczniej.
          <br />Agenci AI · EUDR · Szkolenia · Oprogramowanie · Strony WWW · SEO
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Link
            to="/kontakt"
            className="group relative overflow-hidden bg-[#00d4ff] text-black px-10 py-5 rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-[0_0_40px_rgba(0,212,255,0.4)]"
          >
            <span className="relative z-10 flex items-center gap-2">
              Umów konsultację
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity" />
          </Link>
          <Link
            to="/kontakt"
            className="group flex items-center gap-2 px-10 py-5 rounded-xl font-semibold text-base border border-white/20 text-white hover:border-white/50 hover:bg-white/5 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            <i className="ri-mail-send-line text-xl" />
            Napisz do nas
          </Link>
        </div>

        {/* Scroll indicator */}
        <div className="flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/30 text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-white/30 to-transparent" />
        </div>
      </div>

      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes fade-in-down {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-down { animation: fade-in-down 0.8s ease forwards; }
        .animate-fade-in-up { animation: fade-in-up 0.8s ease forwards; }
      `}</style>
    </section>
  );
}
